<script>
import { ref, computed, nextTick, watch, onMounted } from 'vue';
import MsBaseComponent from '@/components/base/MsBaseComponent.vue';
import { useTimeout } from '@/setup/commonComposition.js';
import MsPopover from '@/components/popover/MsPopover.vue';
import commonFn from '@/commons/commonFunction.js';
import {
  useValidateControl,
  ControlValidateClass,
} from '@/setup/validateControl.js';
import MsComboboxItem from '@/components/combobox/MsComboboxItemSingle.vue';
import { ValidateRules } from '@/setup/validateControl.js';

const queryKey = 'queryKey';

const scrollKey = 'scrollKey';

export default {
  components: {
    MsPopover,
    MsComboboxItem,
  },
  extends: MsBaseComponent,
  props: {
    /**
     * Có hiện icon mở combobox ko
     */
    isShowExpandIcon: {
      type: Boolean,
      default: true,
    },
    /**
     * Giá trị của combobox
     */
    modelValue: {
      type: Array,
      default: () => {},
    },
    /**
     * Hàm thực hiện query
     */
    queryFunction: {
      type: Function,
      default: () => {},
    },
    /**
     * Trường chứa ID
     */
    valueField: {
      type: String,
      required: true,
    },
    /**
     * Trường để hiển thị
     */
    displayField: {
      type: String,
      required: true,
    },
    /**
     * placeholder của input
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * Có focus item đầu tiên khi mở popover ko
     */
    isFocusTheFirstItem: {
      type: Boolean,
      default: true,
    },
    /**
     * Có mở popover khi focus input ko
     */
    openPopoverWhenFocusInput: {
      type: Boolean,
      default: false,
    },
    /**
     * Số item hiển thị tối đa trong view của popover dùng để xét height cho popover
     */
    maxItemsDisplay: {
      type: Number,
      default: 5,
    },
    /**
     * Chiều cao của item
     */
    itemHeight: {
      type: Number,
      default: 36,
    },
    /**
     * Chều rộng của popover nếu ko truyền sẽ lấy theo độ rộng của combobox
     */
    widthPopover: {
      type: Number,
      default: 0,
    },
    /**
     * Text hiển thị khi ko có dữ liệu
     */
    noDataText: {
      type: String,
      default: 'Không có dữ liệu',
    },
    /**
     * Xác định xem input có border hay không
     */
    hasBorder: {
      type: Boolean,
      default: true,
    },
    /**
     * Xác định xem icon expand có border hay không
     */
    hasIconBorder: {
      type: Boolean,
      default: false,
    },
    /**
     * Loại query (remote hoặc local)
     */
    queryMode: {
      type: String,
      default: 'local',
    },
    /**
     * Dữ liệu nếu dùng query là local
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * Left icon
     */
    leftIcon: {
      type: String,
      default: '',
    },
    /**
     * Right icon
     */
    rightIcon: {
      type: String,
      default: '',
    },
    /**
     * Tooltip của right icon
     */
    rightIconTooltip: {
      type: String,
      default: '',
    },
    /**
     * Chỉ cho phép chọn
     */
    selectOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Cho phép free text
     */
    allowFreeText: {
      type: Boolean,
      default: false,
    },
    /**
     * Kích thước trang mỗi lần query remote
     */
    pageSize: {
      type: Number,
      default: 10,
    },
    /**
     * Số item tối thiểu phải chọn (Nếu chỉ còn số item này thì sẽ ko thể bỏ chọn)
     */
    minSelectedItem: {
      type: Number,
      default: 0,
    },
    /**
     * Custom style cho popover
     */
    customStylePopover: {
      type: [Object, String],
      default: null,
    },
    /**
     * Custom class cho danh sách item
     */
    customClassItems: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'selected', 'onClickRightIcon'],
  setup(props, { emit, slots }) {
    /**
     * ref của div main
     */
    const mainRef = ref(null);

    /**
     * ref của input
     */
    const inputRef = ref(null);

    /**
     * ref của popover
     */
    const popoverRef = ref(null);

    /**
     * Trạng thái bật tắt popover
     */
    const isActive = ref(false);

    /**
     * Trạng thái loading
     */
    const isLoading = ref(false);

    /**
     * Dữ liệu của combobox
     */
    const dataSource = ref([]);

    /**
     * Tổng Item
     */
    const totalRecords = ref(0);

    /**
     * Từ khóa tìm kiếm
     */
    const searchText = ref('');

    /**
     * Org ID đang đc hover
     */
    const hoverIdx = ref(-1);

    /**
     * Độ rộng popover
     */
    const widthOfPopover = ref(0);

    /**
     * param truyền ra để query
     */
    const queryParam = ref({
      searchText: '',
      pageIndex: 1,
      pageSize: props.pageSize,
    });

    const {
      errorMessage,
      isValidate,
      validated,
      validate,
    } = useValidateControl({ props }, inputRef);

    const { runTimeoutFunc } = useTimeout();

    /**
     * Có phải là required ko
     * @author: BMThang(18/11/2022)
     */
    const isRequired = computed(() => {
      if (!props.rules?.length) {
        return false;
      }

      const idxOfRequiredRule = props.rules.findIndex(
        (rule) => rule.name === ValidateRules.required
      );
      return idxOfRequiredRule >= 0;
    });

    /**
     * Danh sách ID của các item đc select
     * @author: BMThang(18/11/2022)
     */
    const selectedItemIds = computed(() => {
      return props.modelValue.map((item) => item[props.valueField]);
    });

    /**
     * Chiều cao của popover
     */
    const heightOfPopover = computed(() => {
      return parseInt(props.maxItemsDisplay * props.itemHeight) + 16;
    });

    /**
     * Format lại placeholder
     * @author: BMThang(18/11/2022)
     */
    const inputPlaceholder = computed(() => {
      return props.modelValue.length ? '' : props.placeholder;
    });

    /**
     * Có truyền slot item hay ko
     * @author: BMThang(06/11/2022)
     */
    const hasSlotItem = computed(() => {
      return slots['item'] ? true : false;
    });

    /**
     * Có truyền slot item hay ko
     * @author: BMThang(06/11/2022)
     */
    const hasSlotSelectedItem = computed(() => {
      return slots['selected-item'] ? true : false;
    });

    /**
     * Tính chiều rộng của popover
     * @author: BMThang(18/11/2022)
     */
    const calculateWidthOfPopover = () => {
      if (mainRef.value && !props.widthPopover) {
        const { width } = mainRef.value.getBoundingClientRect();
        widthOfPopover.value = parseInt(width);
        return;
      }
      widthOfPopover.value = props.widthPopover || 300;
      return;
    };

    /**
     * Xử lý khi outfocus combobox
     * @author: BMThang(18/11/2022)
     */
    const handleOutFocus = () => {
      // Nếu đc validate và đã validate 1 lần rồi thì sẽ validate
      if (isValidate.value && validated.value) {
        validate();
      }
    };

    /**
     * Query data để lấy dữ liệu
     * @param {string} searchText từ khóa tìm kiếm
     * @param {boolean} isLoadMore Có phải load thêm ko
     * @author: BMThang(18/11/2022)
     */
    const handleQueryRemote = async (searchText = '', isLoadMore = false) => {
      isLoading.value = true;
      queryParam.value = {
        ...queryParam.value,
        searchText,
      };
      try {
        const res = await props.queryFunction(queryParam.value);
        if (!res) {
          dataSource.value = [];
          return;
        }

        const { pageData, totalRecords: total } = res;

        if (Array.isArray(pageData)) {
          totalRecords.value = total;

          if (isLoadMore) {
            dataSource.value = [...dataSource.value, ...pageData];
          } else {
            dataSource.value = [...pageData];
            // Xét hover vào item đầu tiên
            if (props.isFocusTheFirstItem) {
              hoverIdx.value = 0;
            }
            // Xóa hover
            else {
              hoverIdx.value = -1;
            }
          }
        }
      } catch (err) {
        console.log(err);
        // Nếu có lỗi thì clear hết dữ liệu
        dataSource.value = [];
        hoverIdx.value = -1;
      }
      isLoading.value = false;
    };

    /**
     * Hàm thực hiện query local
     * @param {string} searchText từ khóa tìm kiếm
     * @author: BMThang(25/11/2022)
     */
    const handleQueryLocal = (searchText = '') => {
      queryParam.value.searchText = searchText;
      let items = JSON.parse(JSON.stringify(props.data));

      const formattedSearchText = commonFn.removeVietnameseTones(
        searchText.trim().toLowerCase()
      );

      // lọc
      items = items.filter((item) => {
        let itemName = `${item[props.displayField]}`.trim().toLowerCase();
        itemName = commonFn.removeVietnameseTones(itemName);
        return itemName.includes(formattedSearchText);
      });

      dataSource.value = [...items];

      // Xét hover vào org đầu tiên
      if (props.isFocusTheFirstItem) {
        hoverIdx.value = 0;
      }
      // Xóa hover
      else {
        hoverIdx.value = -1;
      }
    };

    /**
     * Thực hiện query
     * @param {string} searchText từ khóa tìm kiếm
     * @param {boolean} isLoadMore Có phải load thêm ko
     * @author: BMThang(27/11/2022)
     */
    const handleQuery = (searchText = '', isLoadMore = false) => {
      if (props.queryMode === 'remote') {
        return handleQueryRemote(searchText, isLoadMore);
      }
      return handleQueryLocal(searchText);
    };

    /**
     * Đặt lại vị trí của popover. Chỉ khi bật popover
     * @author: BMThang(30/11/2022)
     */
    const setPopoverPosition = () => {
      if (popoverRef.value && isActive.value) {
        popoverRef.value.calculatePopoverPosition();
      }
    };

    /**
     * Xử lý khi chọn 1 item
     * @param {object} item dữ liệu item đc chọn
     * @param {boolean} isSelected có chọn ko
     * @author: BMThang(18/11/2022)
     */
    const handleSelectItem = (item, isSelected) => {
      const oldData = JSON.parse(JSON.stringify(props.modelValue));

      // focus vào input
      focusInput();

      let valueEmit = [];

      // Nếu uncheck thì xóa khỏi danh sách
      // if (!isSelected) {
      //   // Nếu có số item tối thiểu và số item đang select nhỏ hơn hoặc bằng số item tồi thiểu thì ko thể bỏ chọn nữa
      //   if (
      //     props.minSelectedItem > 0 &&
      //     props.modelValue.length <= props.minSelectedItem
      //   ) {
      //     closePopover();
      //     return;
      //   }
      //   // Xóa khỏi danh sách
      //   valueEmit = [];
      // }
      // Nếu check thì thêm vào
      // else {
      // Thêm org đc select
      valueEmit = [item];
      // }

      const metaData = {
        oldData,
        newData: valueEmit,
      };

      emit('update:modelValue', valueEmit);
      emit('selected', metaData);

      nextTick(() => {
        closePopover();
      });
    };

    /**
     * Focus vào input
     * @author: BMThang(18/11/2022)
     */
    const focusInput = () => {
      if (inputRef.value && typeof inputRef.value.focus === 'function') {
        inputRef.value.focus();
      }
    };

    /**
     * Đóng popover
     * @author: BMThang(18/11/2022)
     */
    const closePopover = () => {
      const oldData = JSON.parse(JSON.stringify(props.modelValue));
      const metaData = {
        oldData,
        newData: null,
      };

      isActive.value = false;
      // Xóa hover id
      hoverIdx.value = -1;

      // Xử lý outfocus
      handleOutFocus();

      // Nếu giá trị input là rỗng thì emit mảng rỗng (ko chọn giá trị) và số item đc chọn tối thiểu
      if (searchText.value.trim() === '' && props.minSelectedItem === 0) {
        metaData.newData = [];
        emit('update:modelValue', []);
        emit('selected', metaData);
        return;
      }

      // Nếu giá trị search text ko rỗng và cho phép free text thì emit search text ra ngoài
      if (props.allowFreeText === true) {
        const newData = [
          {
            [props.valueField]: 'ms-combobox-tree-single-free-text',
            [props.displayField]: searchText.value.trim(),
          },
        ];
        metaData.newData = newData;
        emit('update:modelValue', newData);
        emit('selected', metaData);
        return;
      }

      // Nếu giá trị search text ko rỗng và có giá trị đc chọn thì fill input với giá trị đc chọn
      if (props.modelValue.length) {
        searchText.value = props.modelValue[0][props.displayField];
        return;
      }

      // Nếu giá trị search text ko rỗng và ko có giá trị đc chọn thì fill input với string rỗng
      searchText.value = '';
    };

    /**
     * Focus vào item đc chọn đầu tiên.
     * Chỉ thực hiện khi queryMode = local.
     * @author: BMThang(26/11/2022)
     */
    const focusTheFirstSelectedItem = () => {
      // Nếu là query local và đã có giá trị thì mở giá trị ra
      if (props.queryMode === 'local' && props.modelValue.length) {
        // Scroll đển item đc chọn đầu tiên
        nextTick(() => {
          runTimeoutFunc(
            () => {
              const theFirstSelectedItem = props.modelValue[0];
              const idxOfTheFirstSelectedItem = dataSource.value.findIndex(
                (item) =>
                  item[props.valueField] ===
                  theFirstSelectedItem[props.valueField]
              );
              // Nếu ko tìm thấy thì thôi
              if (idxOfTheFirstSelectedItem === -1) {
                return;
              }
              hoverIdx.value = idxOfTheFirstSelectedItem;
              scrollToHoverItem(true);
            },
            300,
            scrollKey
          );
        });
      }
    };

    /**
     * Mở popover
     * @author: BMThang(18/11/2022)
     */
    const openPopover = () => {
      // Nếu bị disabled thì thôi
      if (props.disabled || props.readOnly) {
        return;
      }

      calculateWidthOfPopover();

      focusInput();
      // Nếu mở rồi thì thôi
      if (isActive.value) {
        return;
      }
      isActive.value = true;

      // Comment lại vì muốn khi mở ra sẽ hiện toàn bộ item
      // // Nếu có dữ liệu rồi và search text = search text lúc trước thì ko query nữa
      // if (dataSource.value.length && searchText.value === queryParam.value.searchText) {
      //   // Mở toàn bộ cha của các item đc chọn và Focus vào item đc chọn đầu tiên
      //   // Chỉ thực hiện khi queryMode = local
      //   focusTheFirstSelectedItem();
      //   return;
      // }

      // Query để lấy dữ liệu
      runTimeoutFunc(
        () => {
          // Xét pageIndex về 1
          queryParam.value.pageIndex = 1;

          handleQuery()?.then(() => {
            setPopoverPosition();
          });

          if (props.queryMode === 'local') {
            setPopoverPosition();
          }

          // Mở toàn bộ cha của các item đc chọn và Focus vào item đc chọn đầu tiên
          // Chỉ thực hiện khi queryMode = local
          focusTheFirstSelectedItem();
        },
        0,
        queryKey
      );
    };

    /**
     * Xử lý khi focus vào input
     * @author: BMThang(18/11/2022)
     */
    const handleFocusInput = () => {
      if (props.openPopoverWhenFocusInput) {
        openPopover();
      }
    };

    /**
     * Toggle popover
     * @author: BMThang(18/11/2022)
     */
    const togglePopover = () => {
      if (isActive.value) {
        closePopover();
        return;
      }
      openPopover();
    };

    /**
     * Xử lý click icon
     * @author: BMThang(18/11/2022)
     */
    const handleClickLeftIcon = () => {
      // Nếu chỉ cho phép chọn thì mở popover
      if (props.selectOnly) {
        if (!isActive.value) {
          openPopover();
        }
        return;
      }
      // Ngược lại thi focus vào input
      focusInput();
    };

    /**
     * Xử lý click right icon
     * @author: BMThang(30/03/2023)
     */
    const handleClickRightIcon = () => {
      // Nếu chỉ cho phép chọn thì mở popover
      if (props.selectOnly) {
        if (!isActive.value) {
          openPopover();
        }
        return;
      }
      // Ngược lại thi focus vào input
      focusInput();
      emit('onClickRightIcon');
    };

    /**
     * Xử lý click input container
     * @author: BMThang(18/11/2022)
     */
    const handleClickInputContainer = () => {
      // Nếu chỉ cho phép chọn thì mở popover
      if (props.selectOnly) {
        if (!isActive.value) {
          openPopover();
        }
        return;
      }
      // Ngược lại thi focus vào input
      focusInput();
    };

    /**
     * Scroll lên cùng của popover
     * @author: BMThang(18/11/2022)
     */
    const scrollToTop = () => {
      if (popoverRef.value?.popoverRef) {
        const popoverContentEl = popoverRef.value.popoverRef.querySelector(
          '.ms-popover-in-component-content'
        );
        if (typeof popoverContentEl.scrollTo === 'function') {
          popoverContentEl.scrollTo(0, 0);
        }
      }
    };

    /**
     * Xử lý khi thay đổi từ khóa tìm kiếm
     * @param {object} e event object
     * @author: BMThang(18/11/2022)
     */
    const handleChangeSearchText = (e) => {
      const value = e.target.value;
      searchText.value = value;
      queryParam.value.pageIndex = 1;

      // Nếu chưa mở popover thì mở
      if (!isActive.value) {
        openPopover();
      }

      // Query để lấy dữ liệu
      runTimeoutFunc(
        () => {
          handleQuery(searchText.value)?.then(() => {
            setPopoverPosition();
            scrollToTop();
          });

          if (props.queryMode === 'local') {
            handleQuery(searchText.value);
            setPopoverPosition();
            scrollToTop();
          }
        },
        400,
        queryKey
      );
    };

    /**
     * Gán hoverIdx khi item đc hover
     * @param {object} item item đc hover
     * @author: BMThang(18/11/2022)
     */
    const handleHoverItem = (idx) => {
      hoverIdx.value = idx;
    };

    /**
     * Scroll item đc hover vào trong view của container
     * @param {boolean} alignTopTop có căn element lên top của container ko
     * @author: BMThang(18/11/2022)
     */
    const scrollToHoverItem = (alignTopTop = false) => {
      if (!popoverRef.value) {
        return;
      }
      const itemsEl = popoverRef.value.popoverRef;
      if (!itemsEl || hoverIdx.value === -1) {
        return;
      }

      // Tìm item dựa vào idx
      const hoverItem = itemsEl.querySelector(
        `#ms-combobox-tree-multiple-item-${hoverIdx.value}`
      );
      // Ko tìm thấy thì thôi
      if (!hoverItem) {
        return;
      }

      let {
        top: containerTop,
        height: containerHeight,
      } = itemsEl.getBoundingClientRect();
      const {
        top: itemTop,
        height: itemHeight,
      } = hoverItem.getBoundingClientRect();
      containerHeight -= 16;

      // Nếu vẫn trong view thì ko cần scroll
      if (
        itemTop >= containerTop &&
        itemTop + itemHeight <= containerTop + containerHeight
      ) {
        return;
      }

      if (typeof hoverItem.scrollIntoView === 'function') {
        hoverItem.scrollIntoView(alignTopTop);
      }
    };

    /**
     * Load thêm dữ liệu Remote
     * @author: BMThang(30/11/2022)
     */
    const loadMore = () => {
      // Tăng pageIndex
      queryParam.value.pageIndex += 1;

      // Nếu model value có tên hiển thị = giá trị hiện tại của input thì cho searchText thành rỗng để query tất cả
      let currentSearchText = searchText.value;
      if (props.modelValue.length) {
        if (searchText.value === props.modelValue[0][props.displayField]) {
          currentSearchText = '';
        }
      }

      return handleQueryRemote(currentSearchText, true);
    };

    /**
     * Hàm đệ xử lý khi arrow down
     * @author: BMThang(18/11/2022)
     */
    const handleArrowDown = () => {
      // Nếu chưa mở popover thì mở
      if (!isActive.value) {
        openPopover();
        return;
      }

      // Nếu ko có org nào đc hover thì hover org đầu tiên
      if (hoverIdx.value === -1) {
        hoverIdx.value = 0;
        return;
      }

      const newItemIdx = hoverIdx.value + 1;

      // Nếu next item ở trong dataSource thì next
      if (newItemIdx < dataSource.value.length) {
        hoverIdx.value = newItemIdx;
        scrollToHoverItem();
        return;
      }

      // Nếu next item ko ở trong dataSource, là remote, chưa chạm đến totalRecords thì load thêm
      if (
        props.queryMode === 'remote' &&
        dataSource.value.length < totalRecords.value
      ) {
        loadMore().then(() => {
          scrollToHoverItem();
        });
      }
    };

    /**
     * Xử lý khi arrow up
     * @author: BMThang(18/11/2022)
     */
    const handleArrowUp = () => {
      // Nếu chưa mở popover thì mở
      if (!isActive.value) {
        openPopover();
        return;
      }

      // Nếu ko có org nào đc hover thì hover org cuối
      if (hoverIdx.value === -1) {
        hoverIdx.value = dataSource.value.length - 1;
        return;
      }

      const newItemIdx = hoverIdx.value - 1;
      if (newItemIdx >= 0) {
        hoverIdx.value = newItemIdx;
      }
    };

    /**
     * Hàm xử lý Enter
     * @author: BMThang(18/11/2022)
     */
    const handleEnter = () => {
      // Nếu chưa mở popover thì mở
      if (!isActive.value) {
        openPopover();
        return;
      }

      // Nếu chưa hover org nào thì out
      if (hoverIdx.value === -1) {
        return;
      }

      const selectedItem = dataSource.value[hoverIdx.value];
      const isSelected = selectedItemIds.value.includes(
        selectedItem[props.valueField]
      );
      handleSelectItem(selectedItem, !isSelected);
    };

    /**
     * Hàm xử lý khi Tab hoặc Esc
     * @author: BMThang(18/11/2022)
     */
    const handleTabAndEsc = () => {
      // Nếu đang mở popover thì đóng
      if (isActive.value) {
        closePopover();
        return;
      }

      // Xử lý outfocus
      handleOutFocus();
    };

    /**
     * Gọi API lấy thêm dữ liệu nếu scroll tới cuối
     * @param {object} e event object
     * @author: BMThang(16/11/2022)
     */
    const handleScrollBottom = (e) => {
      // Local thì ko xử lý
      if (props.queryMode === 'local') {
        return;
      }
      const target = e.target;
      if (target.scrollTop >= target.scrollHeight - target.offsetHeight) {
        // Chỉ gọi nếu dữ liệu hiện có ít hơn dữ liệu trên server
        if (dataSource.value.length < totalRecords.value) {
          loadMore();
        }
      }
    };

    /**
     * Hàm xử lý keydown
     * @author: BMThang(18/11/2022)
     */
    const handleKeyDown = (e) => {
      const key = e.code;
      if (key === 'ArrowDown') {
        handleArrowDown();
        e.preventDefault();
      } else if (key === 'ArrowUp') {
        handleArrowUp();
        scrollToHoverItem(true);
        e.preventDefault();
      } else if (key === 'Enter') {
        handleEnter();
        e.stopPropagation();
        e.preventDefault();
      } else if (key === 'Tab') {
        handleTabAndEsc();
      } else if (key === 'Escape') {
        handleTabAndEsc();
      }
    };

    /**
     * Trả về value của combobox
     * @author: BMThang(18/11/2022)
     */
    const getValue = () => {
      return props.modelValue;
    };

    /**
     * Focus vào combobox khi có lỗi (Để MsValidate gọi)
     * @author: BMThang(18/11/2022)
     */
    const focusError = () => {
      focusInput();
      openPopover();
    };

    /**
     * Xử lý dữ liệu khi khởi tạo
     * @author: BMThang(18/11/2022)
     */
    const initData = () => {
      if (props.modelValue.length) {
        searchText.value = props.modelValue[0][props.displayField];
        return;
      }
      searchText.value = '';
    };

    onMounted(() => {
      initData();
      calculateWidthOfPopover();
    });

    watch(
      () => props.modelValue,
      () => {
        initData();
      }
    );

    return {
      ControlValidateClass,
      mainRef,
      inputRef,
      popoverRef,
      isActive,
      isLoading,
      dataSource,
      totalRecords,
      searchText,
      hoverIdx,
      widthOfPopover,
      queryParam,
      errorMessage,
      isValidate,
      validated,
      validate,
      isRequired,
      selectedItemIds,
      heightOfPopover,
      inputPlaceholder,
      hasSlotItem,
      hasSlotSelectedItem,
      calculateWidthOfPopover,
      handleOutFocus,
      handleQueryRemote,
      handleQueryLocal,
      handleQuery,
      setPopoverPosition,
      handleSelectItem,
      focusInput,
      closePopover,
      focusTheFirstSelectedItem,
      openPopover,
      handleFocusInput,
      togglePopover,
      handleClickLeftIcon,
      handleClickRightIcon,
      handleClickInputContainer,
      scrollToTop,
      handleChangeSearchText,
      handleHoverItem,
      scrollToHoverItem,
      loadMore,
      handleArrowDown,
      handleArrowUp,
      handleEnter,
      handleTabAndEsc,
      handleScrollBottom,
      handleKeyDown,
      getValue,
      focusError,
      initData,
    };
  },
};
</script>

<template>
  <div
    :class="[
      'ms-combobox-tree-multiple',
      'ms-editor',
      { [ControlValidateClass]: isValidate },
    ]"
  >
    <!-- Label -->
    <label v-if="label" class="label">
      {{ label }}
      <span v-if="isRequired" class="required">*</span>
    </label>

    <!-- Main -->
    <div
      ref="mainRef"
      :class="[
        'ms-combobox-tree-multiple-main',
        { 'ms-combobox-tree-multiple-main-has-border': hasBorder },
        { 'ms-combobox-tree-multiple-main-disabled': disabled || readOnly },
        { 'ms-combobox-tree-multiple-main-error': errorMessage },
        {
          'ms-combobox-tree-multiple-main-is-show-expand-icon':
            isShowExpandIcon || rightIcon,
        },
        { 'ms-combobox-tree-multiple-main-has-left-icon': leftIcon },
        {
          'ms-combobox-tree-multiple-main-select-only':
            selectOnly && modelValue.length,
        },
      ]"
    >
      <!-- Left icon -->
      <div
        v-if="leftIcon"
        class="ms-combobox-tree-multiple-left-icon"
        @click="handleClickLeftIcon"
      >
        <div :class="['icon24', leftIcon]" />
      </div>

      <!-- Input container -->
      <div
        :class="['ms-combobox-tree-multiple-input-container']"
        @click="handleClickInputContainer"
      >
        <div
          v-if="selectOnly"
          class="ms-combobox-tree-multiple-display-text-container"
          @click="handleClickInputContainer"
        >
          <slot
            v-if="hasSlotSelectedItem"
            name="selected-item"
            :selected-items="modelValue"
          />
          <div
            v-else
            class="ms-combobox-tree-multiple-display-text"
            :title="searchText"
          >
            {{ searchText }}
          </div>
        </div>
        <!-- Input -->
        <input
          ref="inputRef"
          :value="searchText"
          :title="searchText"
          :placeholder="inputPlaceholder"
          :class="[
            'ms-combobox-tree-multiple-input',
            { 'ms-combobox-tree-multiple-input-select-only': selectOnly },
          ]"
          :disabled="disabled || readOnly"
          :readonly="selectOnly"
          type="text"
          @focus="handleFocusInput"
          @input="handleChangeSearchText"
          @keydown="handleKeyDown"
        />
      </div>

      <!-- Expand icon -->
      <div
        v-if="isShowExpandIcon"
        :class="[
          'ms-combobox-tree-multiple-expand-icon',
          { 'ms-combobox-tree-multiple-expand-icon-expand': isActive },
          { 'ms-combobox-tree-multiple-expand-icon-bordered': hasIconBorder },
        ]"
        @click="togglePopover"
      />

      <!-- End icon -->
      <div
        v-if="rightIcon"
        :title="rightIconTooltip"
        :class="['ms-combobox-tree-multiple-right-icon', rightIcon]"
        @click="handleClickRightIcon"
      />
    </div>

    <!-- Error -->
    <div v-if="errorMessage" :title="errorMessage" class="error-text">
      {{ errorMessage }}
    </div>

    <!-- Popover -->
    <ms-popover
      ref="popoverRef"
      :target="mainRef"
      :is-active="isActive"
      :width="widthOfPopover"
      :min-height="52"
      :max-height="heightOfPopover"
      :custom-style="customStylePopover"
      transition="ms-side-bottom-top"
      @onClose="closePopover"
      @onScroll="handleScrollBottom"
    >
      <!-- Loading -->
      <transition name="ms-fade">
        <div v-if="isLoading" class="ms-combobox-tree-multiple-loading">
          <div class="loading-svg">
            <div class="effects" />
          </div>
        </div>
      </transition>

      <!-- items -->
      <div
        :class="[
          'ms-combobox-tree-multiple-items',
          {
            'ms-combobox-tree-multiple-items-empty':
              dataSource.length === 0 && !isLoading,
          },
          customClassItems,
        ]"
      >
        <ms-combobox-item
          v-for="(item, idx) in dataSource"
          :key="item[valueField]"
          :item="item"
          :hover-idx="hoverIdx"
          :item-idx="idx"
          :value-field="valueField"
          :display-field="displayField"
          :is-selected="selectedItemIds.includes(item[valueField])"
          @onSelect="handleSelectItem"
          @onHover="handleHoverItem(idx)"
        >
          <template v-if="hasSlotItem" #item="{ itemData, isSelected }">
            <slot name="item" :item-data="itemData" :is-selected="isSelected" />
          </template>
        </ms-combobox-item>
        <div
          v-if="dataSource.length === 0 && !isLoading && noDataText"
          class="ms-combobox-tree-multiple-no-data-text"
        >
          {{ noDataText }}
        </div>
      </div>
    </ms-popover>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.ms-combobox-tree-multiple {
  width: 100%;
  .ms-combobox-tree-multiple-main {
    width: 100%;
    border-radius: $input-border-radius;
    border: 1px solid transparent;
    background-color: $input-background;
    display: flex;
    height: $input-height;
    transition: border-color 0.2s ease;
    .ms-combobox-tree-multiple-left-icon {
      width: $input-height;
      height: $input-height;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      cursor: pointer;
    }
    .ms-combobox-tree-multiple-right-icon {
      width: $input-height;
      height: $input-height;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      cursor: pointer;
    }
    .ms-combobox-tree-multiple-input-container {
      display: flex;
      flex-wrap: wrap;
      flex-grow: 1;
      max-width: 100%;
      min-height: $input-height;
      padding-left: 12px;
      cursor: text;
     overflow: hidden;
      .ms-combobox-tree-multiple-chip {
        height: 32px;
        margin: 2px;
        display: flex;
        align-items: center;
        border-radius: 16px;
        background-color: var(--combobox-chip-bg);
        max-width: calc(100% - 46px);
        border: $input-normal-border;
        .ms-combobox-tree-multiple-chip-name {
          font-size: $font-size-caption;
          font-weight: $font-weight-caption;
          padding: 0 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ms-combobox-tree-multiple-chip-remove {
          flex-shrink: 0;
          width: 22px;
          height: 22px;
          margin-left: -6px;
          margin-right: 5px;
          cursor: pointer;
          background: transparent url($ms-ic-close);
          background-position: center;
          background-repeat: no-repeat;
          background-size: 10px 10px;
        }
      }
      .ms-combobox-tree-multiple-display-text-container {
        display: none;
        .ms-combobox-tree-multiple-display-text {
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
      .ms-combobox-tree-multiple-input {
        outline: none;
        border: none;
        border-radius: $input-normal-border;
        background-color: transparent;
        font-size: $font-size-body;
        font-weight: $font-weight-body;
        line-height: $line-height-body;
        height: $input-height;
        width: 0%;
        flex-grow: 1;
        min-width: 40px;
        padding-right: 8px;
        &::placeholder {
          color: var(--grey-60);
          font-size: $font-size-body;
          font-weight: $font-weight-body;
          line-height: $line-height-body;
        }
        &.ms-combobox-tree-multiple-input-select-only {
          cursor: pointer;
          color: transparent;
          caret-color: transparent;
        }
      }
    }
    .ms-combobox-tree-multiple-expand-icon {
      border-radius: 0 4px 4px 0;
      width: $input-height;
      min-height: $input-height;
      height: auto;
      position: relative;
      flex-shrink: 0;
      border-left: 1px solid transparent;
      cursor: pointer;
      &.ms-combobox-tree-multiple-expand-icon-bordered {
        border-left: $input-normal-border;
      }
      &::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        display: inline-block;
        background: transparent url($ms-ic-arrow-down) no-repeat center;
        top: 0px;
        left: 0px;
        transform: rotate(0deg);
        transition: transform 0.2s ease;
      }
    }
    &.ms-combobox-tree-multiple-main-select-only {
      .ms-combobox-tree-multiple-input-container {
        cursor: pointer;
        .ms-combobox-tree-multiple-display-text-container {
          display: flex;
          align-items: center;
          background-color: transparent;
          font-size: $font-size-body;
          font-weight: $font-weight-body;
          line-height: $line-height-body;
          height: $input-height;
          width: calc(100% - 2px);
        }
        .ms-combobox-tree-multiple-input {
          cursor: pointer;
          width: 1px;
          overflow: hidden;
          flex-grow: 0;
          min-width: unset;
          padding: 0;
          opacity: 0;
        }
      }
    }
    &.ms-combobox-tree-multiple-main-has-left-icon {
      .ms-combobox-tree-multiple-input-container {
        padding-left: 2px;
        max-width: calc(100% - ($input-height + $input-height));
        .ms-combobox-tree-multiple-chip {
          max-width: calc(100% - (44px));
        }
      }
    }
    &.ms-combobox-tree-multiple-main-has-border {
      border: $input-normal-border;
    }
    &:hover,
    &:focus-within {
      border-color: $input-hover-border-color;
    }
    &.ms-combobox-tree-multiple-main-disabled {
      background-color: $input-disabled-bg;
      border-color: $input-disabled-border-color;
    }
    &.ms-combobox-tree-multiple-main-error {
      border: 1px solid $input-error-border-color;
    }
    &.ms-combobox-tree-multiple-main-is-show-expand-icon {
      .ms-combobox-tree-multiple-input-container {
        max-width: calc(100% - $input-height);
      }
    }
  }
}

.ms-combobox-tree-multiple-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.4);
  z-index: 1;
}

.ms-combobox-tree-multiple-items {
  width: 100%;
  height: 100%;
  overflow: auto;
  .ms-combobox-tree-multiple-no-data-text {
    font-size: $font-size-body;
    font-weight: $font-weight-body;
    color: $combobox-no-data-text-color;
    height: $input-height;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>